import type { ConversationSalesContext } from '@/services/conversationsApi'

export interface ParsedBotSummaryItem {
  name: string
  quantity: number
  price?: number
}

export interface ParsedBotSummaryHandoff {
  isOrderConfirmed: boolean
  confirmationBadgeText: string
  confirmationDescription: string
  orderNumber: string | null
  totalAmount: number | null
  formattedTotal: string | null
  currency: string
  customerName: string | null
  customerPhone: string | null
  fulfillmentMethod: 'pickup' | 'delivery' | 'dine_in' | 'appointment' | null
  fulfillmentAddress: string | null
  paymentMethod: string | null
  items: ParsedBotSummaryItem[]
  escalationReason: {
    label: string
    detail?: string
    code?: string
  } | null
  actionRequired: string | null
  lastCustomerMessage: string | null
  rawContent: string
}

const CONFIRMED_PATTERNS = [
  /PEDIDO \/ VENTA CONFIRMADA/i,
  /PEDIDO CONFIRMADO/i,
  /PEDIDO REGISTRADO/i,
  /Pedido confirmado/i,
  /trigger de cierre:\s*explicit_phrase/i,
  /trigger de cierre:\s*slot_ledger_complete/i,
]

const ESCALATION_REASONS_HUMAN: Record<string, { label: string; detail: string }> = {
  customer_frustration: {
    label: 'Atención prioritaria',
    detail: 'El cliente presentó dudas o inconformidad y requiere atención personalizada.',
  },
  billing_coordination: {
    label: 'Cierre y cobro de venta',
    detail: 'El cliente confirmó su compra y está listo para coordinar método de pago y despacho.',
  },
  support_refund_confirmed_order: {
    label: 'Post-venta (Garantía o Devolución)',
    detail: 'El cliente solicita atención sobre una compra previa (garantía, cambio o devolución).',
  },
  support_refund: {
    label: 'Post-venta / Garantía',
    detail: 'Consulta sobre políticas de garantía o devolución.',
  },
  explicit_human_request: {
    label: 'Solicitud de asesor humano',
    detail: 'El cliente solicitó expresamente ser atendido por un vendedor o asesor humano.',
  },
  unanswered_checkout_question: {
    label: 'Consulta de cierre de venta',
    detail: 'Preguntas específicas sobre pago, despacho o condiciones para concretar la compra.',
  },
  insufficient_stock: {
    label: 'Consulta de disponibilidad',
    detail: 'El producto solicitado no cuenta con stock inmediato disponible.',
  },
  price_haggling_limit: {
    label: 'Negociación comercial',
    detail: 'El cliente solicitó descuentos o precios especiales que requieren aprobación humana.',
  },
  repeated_failure: {
    label: 'Consulta compleja',
    detail: 'El bot requirió asistencia humana para atender la solicitud con precisión.',
  },
  appointment_required: {
    label: 'Confirmación de cita',
    detail: 'El cliente solicitó agendar una cita o turno que requiere confirmación manual.',
  },
  high_purchase_signal: {
    label: 'Alta intención de compra',
    detail: 'El cliente demostró alto interés comercial y está listo para la atención del vendedor.',
  },
}

export function parseBotSummaryHandoff(
  rawContent: string = '',
  salesContext?: ConversationSalesContext | null
): ParsedBotSummaryHandoff {
  const content = (rawContent || '').trim()

  // 1. Detectar si el pedido está confirmado o no
  const hasConfirmedKeyword = CONFIRMED_PATTERNS.some((pat) => pat.test(content))
  const draftHasConfirmedItem = salesContext?.activeOrderDraft?.items?.some(
    (it: any) => it.status === 'confirmed'
  )
  const draftIsConfirmed =
    salesContext?.activeOrderDraft?.status === 'confirmed' ||
    salesContext?.activeOrderDraft?.status === 'pending_confirmation'

  const isOrderConfirmed = Boolean(
    hasConfirmedKeyword || draftHasConfirmedItem || (draftIsConfirmed && content.includes('• Orden:'))
  )

  // 2. Extraer número de orden
  const orderNumberMatch =
    content.match(/•\s*Orden:\s*#?([A-Za-z0-9_-]+)/i) ||
    content.match(/Pedido confirmado \(#?([A-Za-z0-9_-]+)\)/i)
  const orderNumber = orderNumberMatch
    ? orderNumberMatch[1]
    : (salesContext?.activeOrderDraft as any)?.id || null

  // 3. Extraer total
  let totalAmount: number | null = null
  const totalMatch =
    content.match(/•\s*Total:\s*\$?([\d.,]+)/i) ||
    content.match(/-\s+\*\*Total estimado\*\*:\s*\$?([\d.,]+)/i)
  if (totalMatch && totalMatch[1]) {
    const parsed = parseFloat(totalMatch[1].replace(/,/g, ''))
    if (!isNaN(parsed)) totalAmount = parsed
  } else if (salesContext?.handoff?.totalAmount != null) {
    totalAmount = salesContext.handoff.totalAmount
  } else if (salesContext?.activeOrderDraft?.totalCents != null) {
    totalAmount = salesContext.activeOrderDraft.totalCents / 100
  } else if (salesContext?.product?.price != null) {
    const parsed = parseFloat(String(salesContext.product.price))
    if (!isNaN(parsed)) totalAmount = parsed
  }


  // 4. Extraer cliente (nombre y teléfono)
  const clientMatch = content.match(/•\s*Cliente:\s*([^(•\n\r]+)(?:\(([^)]+)\))?/i)
  const markdownNameMatch = content.match(/-\s+\*\*Nombre\*\*:\s*([^\n\r]+)/i)
  const markdownPhoneMatch = content.match(/-\s+\*\*Telefono\*\*:\s*([^\n\r]+)/i)

  const customerName =
    clientMatch?.[1]?.trim() ||
    markdownNameMatch?.[1]?.trim() ||
    salesContext?.handoff?.customerName ||
    null

  const customerPhone =
    clientMatch?.[2]?.trim() ||
    markdownPhoneMatch?.[1]?.trim() ||
    salesContext?.handoff?.customerPhone ||
    null

  // 5. Método de entrega y dirección
  const methodMatch =
    content.match(/•\s*Método de entrega:\s*([^\n\r]+)/i) ||
    content.match(/-\s+\*\*Método acordado\*\*:\s*([^\n\r]+)/i)
  const rawMethod = (
    methodMatch?.[1] ||
    salesContext?.handoff?.fulfillmentMethod ||
    salesContext?.activeOrderDraft?.fulfillmentMethod ||
    ''
  ).toLowerCase()

  let fulfillmentMethod: 'pickup' | 'delivery' | 'dine_in' | 'appointment' | null = null
  if (rawMethod.includes('retiro') || rawMethod.includes('tienda') || rawMethod === 'pickup') {
    fulfillmentMethod = 'pickup'
  } else if (rawMethod.includes('delivery') || rawMethod.includes('domicilio') || rawMethod === 'delivery') {
    fulfillmentMethod = 'delivery'
  } else if (rawMethod.includes('local') || rawMethod.includes('dine_in')) {
    fulfillmentMethod = 'dine_in'
  } else if (rawMethod.includes('cita') || rawMethod.includes('presencial')) {
    fulfillmentMethod = 'appointment'
  }

  const addressMatch =
    content.match(/•\s*Dirección:\s*([^\n\r]+)/i) ||
    content.match(/-\s+\*\*Dirección \/ Ubicación\*\*:\s*([^\n\r]+)/i)
  const fulfillmentAddress =
    addressMatch?.[1]?.trim() ||
    salesContext?.handoff?.fulfillmentAddress ||
    salesContext?.activeOrderDraft?.fulfillmentAddress ||
    null

  // 6. Método de pago
  const paymentMatch = content.match(/•\s*Método de pago(?: elegido)?:\s*([^\n\r]+)/i)
  const paymentMethod = paymentMatch?.[1]?.trim() || null

  // 7. Ítems
  const items: ParsedBotSummaryItem[] = []
  const bulletItemsMatch = content.match(/•\s*Productos:\s*([^\n\r]+)/i)
  const markdownItemsMatch = content.match(/-\s+\*\*Ítems\*\*:\s*([^\n\r]+)/i)

  if (bulletItemsMatch && bulletItemsMatch[1]) {
    const rawList = bulletItemsMatch[1].split(',')
    for (const raw of rawList) {
      const trimmed = raw.trim()
      const qtyMatch = trimmed.match(/x\s*(\d+)$/i)
      const qty = qtyMatch ? parseInt(qtyMatch[1], 10) : 1
      const name = trimmed.replace(/\s*x\s*\d+$/i, '').trim()
      if (name) items.push({ name, quantity: isNaN(qty) ? 1 : qty })
    }
  } else if (markdownItemsMatch && markdownItemsMatch[1]) {
    const rawList = markdownItemsMatch[1].split(',')
    for (const raw of rawList) {
      const trimmed = raw.trim()
      const qtyMatch = trimmed.match(/\(x(\d+)\)/i)
      const qty = qtyMatch ? parseInt(qtyMatch[1], 10) : 1
      const name = trimmed.replace(/\(x\d+\)/i, '').trim()
      if (name) items.push({ name, quantity: isNaN(qty) ? 1 : qty })
    }
  } else if (salesContext?.handoff?.items?.length) {
    for (const it of salesContext.handoff.items) {
      items.push({
        name: it.name,
        quantity: it.quantity || 1,
        price: it.price,
      })
    }
  } else if (salesContext?.activeOrderDraft?.items?.length) {
    for (const it of salesContext.activeOrderDraft.items) {
      items.push({
        name: it.title || 'Producto',
        quantity: it.quantity || 1,
        price: it.unitPriceCents ? it.unitPriceCents / 100 : undefined,
      })
    }
  } else {
    // Buscar en ### Producto seleccionado
    const prodSection = content.split('### Producto seleccionado')[1]
    if (prodSection) {
      const prodNameMatch = prodSection.match(/-\s+\*\*Nombre\*\*:\s*([^\n\r]+)/i)
      const prodPriceMatch = prodSection.match(/-\s+\*\*Precio\*\*:\s*\$?([\d.,]+)/i)
      if (prodNameMatch && prodNameMatch[1]) {
        items.push({
          name: prodNameMatch[1].trim(),
          quantity: 1,
          price: prodPriceMatch ? parseFloat(prodPriceMatch[1].replace(/,/g, '')) : undefined,
        })
      }
    } else if (salesContext?.product?.title) {
      items.push({
        name: salesContext.product.title,
        quantity: 1,
        price: salesContext.product.price ? Number(salesContext.product.price) : undefined,
      })
    }
  }

  if (totalAmount == null && items.length > 0 && items[0]?.price != null) {
    totalAmount = items[0].price
  }

  const formattedTotal = totalAmount != null ? `$${totalAmount.toFixed(2)}` : null

  // 8. Motivo de escalación
  let escalationReason: ParsedBotSummaryHandoff['escalationReason'] = null
  const causeMatch = content.match(/-\s+\*\*Causa\*\*:\s*([^\n\r]+)/i)
  const detailMatch = content.match(/-\s+\*\*Detalle\*\*:\s*([^\n\r]+)/i)
  const triggerMatch = content.match(/•\s*Trigger de cierre:\s*([^\n\r]+)/i)

  if (causeMatch && causeMatch[1]) {
    const rawLabel = causeMatch[1].trim()
    const codeMatch = rawLabel.match(/\(([^)]+)\)$/)
    const code = codeMatch ? codeMatch[1].trim() : undefined
    const cleanLabel = rawLabel.replace(/\([^)]+\)$/, '').trim()
    escalationReason = {
      label: cleanLabel,
      detail: detailMatch?.[1]?.trim(),
      code,
    }
  } else if (triggerMatch && triggerMatch[1]) {
    const rawTrigger = triggerMatch[1].trim()
    const isExplicit = rawTrigger.includes('explicit_phrase')
    escalationReason = {
      label: isExplicit ? 'Confirmación explícita del cliente' : 'Datos de compra completos',
      detail: isExplicit
        ? 'El cliente confirmó directamente que desea adquirir el producto.'
        : 'Todos los datos de orden y entrega fueron recopilados exitosamente por el bot.',
      code: isExplicit ? 'explicit_phrase' : 'slot_ledger_complete',
    }
  } else if (salesContext?.handoff?.reason) {
    const reasonKey = salesContext.handoff.reason
    const known = ESCALATION_REASONS_HUMAN[reasonKey]
    escalationReason = {
      label: known?.label || reasonKey,
      detail: known?.detail || salesContext.handoff.reasonDetail,
      code: reasonKey,
    }
  }

  // 9. Acción requerida / recomendada
  const actionMatch =
    content.match(/⚠️\s*Acción requerida:\s*([^\n\r]+)/i) ||
    content.match(/### 🎯 Acción recomendada para el vendedor\s*\n+([^\n\r]+)/i) ||
    content.match(/-\s+\*\*Acción requerida\*\*:\s*([^\n\r]+)/i)
  const actionRequired =
    actionMatch?.[1]?.trim() ||
    salesContext?.handoff?.actionRequired ||
    (isOrderConfirmed
      ? 'Validar pago con el cliente y despachar la orden.'
      : 'Asistir al cliente para resolver dudas y concretar la venta.')

  // 10. Último mensaje del cliente
  const lastMsgMatch = content.match(/### 💬 Último mensaje del cliente\s*\n+>\s*"([^"]+)"/i)
  const lastCustomerMessage = lastMsgMatch?.[1]?.trim() || null

  const confirmationBadgeText = isOrderConfirmed
    ? orderNumber
      ? `PEDIDO CONFIRMADO #${orderNumber}`
      : 'PEDIDO CONFIRMADO'
    : 'PEDIDO NO CONFIRMADO (CONSULTA / EN PROCESO)'

  const confirmationDescription = isOrderConfirmed
    ? 'El pedido fue confirmado por el cliente y registrado en el sistema. Procede a coordinar el cobro y la entrega.'
    : 'El cliente NO ha confirmado la compra aún. La conversación requiere asesoría comercial para cerrar la venta.'

  return {
    isOrderConfirmed,
    confirmationBadgeText,
    confirmationDescription,
    orderNumber,
    totalAmount,
    formattedTotal,
    currency: 'USD',
    customerName,
    customerPhone,
    fulfillmentMethod,
    fulfillmentAddress,
    paymentMethod,
    items,
    escalationReason,
    actionRequired,
    lastCustomerMessage,
    rawContent: content,
  }
}
